import { api } from '@/config/api';

export type DiningTableShape = 'square' | 'round' | 'booth';
export type FloorStatus = 'seated' | 'finished' | 'no_show';
export type FloorState = 'free' | 'reserved_soon' | 'seated' | 'no_show';

export interface DiningTable {
  tableUid: string;
  name: string;
  area: string | null;
  seats: number;
  shape: DiningTableShape;
  isActive: boolean;
  sortOrder: number;
}

export interface FloorBookingChip {
  bookingUid: string;
  bookingNo: string;
  customerName: string | null;
  partySize: number | null;
  slotStart: string;
  slotEnd: string;
  status: string;
  floorStatus: FloorStatus | null;
}

export interface FloorTableCard {
  tableUid: string;
  name: string;
  area: string | null;
  seats: number;
  shape: DiningTableShape;
  sortOrder: number;
  state: FloorState;
  booking: FloorBookingChip | null;
}

export interface DayBookingRow {
  bookingUid: string;
  bookingNo: string;
  customerName: string | null;
  customerPhone: string | null;
  partySize: number | null;
  bookingDate: string;
  slotStart: string;
  slotEnd: string;
  status: string;
  floorStatus: FloorStatus | null;
  tableUid: string | null;
  tableName: string | null;
  notes: string | null;
}

export interface TablesSummary {
  tablesTotal: number;
  tablesFreeNow: number;
  coversToday: number;
  seatedNow: number;
  noShowsToday: number;
  hasReservationService: boolean;
}

export type DayBookingFilter = 'waiting' | 'seated' | 'finished' | 'no_show' | 'unassigned';

export interface SuggestedTable { tableUid: string; name: string; area: string | null; seats: number }

export const tablesService = {
  list: async (includeInactive = false): Promise<{ items: DiningTable[] }> =>
    (await api.get('/slighter_crm/tables', { params: includeInactive ? { includeInactive: 'true' } : {} })).data,

  create: async (p: { name: string; area?: string; seats: number; shape?: DiningTableShape; isActive?: boolean; sortOrder?: number }) =>
    (await api.post('/slighter_crm/tables', p)).data as { tableUid: string },

  update: async (tableUid: string, p: Partial<{ name: string; area: string; seats: number; shape: DiningTableShape; isActive: boolean; sortOrder: number }>) =>
    (await api.patch(`/slighter_crm/tables/${tableUid}`, p)).data as { tableUid: string },

  remove: async (tableUid: string) => { await api.delete(`/slighter_crm/tables/${tableUid}`); },

  reorder: async (tableUids: string[]) => (await api.patch('/slighter_crm/tables/reorder', { tableUids })).data,

  floor: async (date: string, time: string): Promise<{ date: string; time: string; tables: FloorTableCard[] }> =>
    (await api.get('/slighter_crm/tables/floor', { params: { date, time } })).data,

  bookings: async (date: string, filter?: DayBookingFilter): Promise<{ items: DayBookingRow[] }> =>
    (await api.get('/slighter_crm/tables/bookings', { params: { date, ...(filter ? { filter } : {}) } })).data,

  summary: async (date: string): Promise<TablesSummary> =>
    (await api.get('/slighter_crm/tables/summary', { params: { date } })).data,

  suggest: async (params: { bookingUid?: string; partySize?: number; date?: string; time?: string }): Promise<{ table: SuggestedTable | null }> =>
    (await api.get('/slighter_crm/tables/suggest', { params })).data,

  assign: async (bookingUid: string, tableUid: string, force = false) =>
    (await api.post(`/slighter_crm/tables/bookings/${bookingUid}/assign`, { tableUid, force })).data as { bookingUid: string; tableUid: string },

  unassign: async (bookingUid: string) =>
    (await api.post(`/slighter_crm/tables/bookings/${bookingUid}/unassign`, {})).data as { bookingUid: string },

  setFloorStatus: async (bookingUid: string, floorStatus: FloorStatus) =>
    (await api.post(`/slighter_crm/tables/bookings/${bookingUid}/floor-status`, { floorStatus })).data as { bookingUid: string; floorStatus: FloorStatus; status: string },

  walkIn: async (p: { tableUid: string; partySize: number; customerName?: string; customerPhone?: string; notes?: string; force?: boolean }) =>
    (await api.post('/slighter_crm/tables/walk-in', p)).data as { bookingUid: string; bookingNo: string; tableUid: string },
};

/** 15-min steps across the day, e.g. "06:00".."23:45" — used by the time select. */
export function quarterHourSlots(fromHour = 6, toHour = 23.75): string[] {
  const out: string[] = [];
  for (let m = fromHour * 60; m <= toHour * 60; m += 15) {
    out.push(`${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`);
  }
  return out;
}

export function nearestQuarterHour(d = new Date()): string {
  const rounded = Math.round((d.getHours() * 60 + d.getMinutes()) / 15) * 15;
  return `${String(Math.floor(rounded / 60) % 24).padStart(2, '0')}:${String(rounded % 60).padStart(2, '0')}`;
}
